(function () {
  "use strict";

  const ROOT_SELECTOR = "[data-join-us]";

  const RING_RADIUS = 70;
  const PROGRESS_START_RATIO = 0.35;
  const MEDIA_FULL_RATIO = 0.85;
  const ROTATION_COMPLETE_RATIO = 0.7;
  const DEFAULT_FINAL_START = 0.86;

  // 固定解除直前に「奥へ倒れ込む」演出をつけるためのパラメータ
  // filter は要素の再ラスタライズ（再描画）を伴い、毎フレーム書き換えると
  // 重いためコンポジタだけで完結する transform / opacity のみで演出する。
  const TILT_START_RATIO = 0.92;
  const TILT_MAX_DEG = 10;
  const TILT_MIN_OPACITY = 0.85;
  const PROGRESS_MAX = 1.4;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  function getViewportHeight() {
    return (
      window.visualViewport?.height ||
      window.innerHeight ||
      document.documentElement.clientHeight
    );
  }

  function getMaximumDiameter() {
    return Math.hypot(window.innerWidth, getViewportHeight()) * 1.2;
  }

  function initializeJoinUs(section) {
    if (!section) return;
    if (section.dataset.joinUsInitialized === "true") return;

    const sticky = section.querySelector("[data-join-us-sticky]");
    const background = section.querySelector("[data-join-us-bg]");
    const anchor = section.querySelector("[data-join-us-anchor]");
    const mediaCircle = section.querySelector("[data-join-us-media]");
    const textRing = section.querySelector("[data-join-us-ring]");
    const finalContent = section.querySelector("[data-join-us-final]");

    if (
      !sticky ||
      !background ||
      !anchor ||
      !mediaCircle ||
      !textRing ||
      !finalContent
    ) {
      return;
    }

    section.dataset.joinUsInitialized = "true";

    const finalStart = clamp(
      Number(section.dataset.finalStart) || DEFAULT_FINAL_START,
      0.5,
      0.95
    );

    const mediaFullAt = finalStart * (MEDIA_FULL_RATIO / DEFAULT_FINAL_START);
    const rotationCompleteAt =
      finalStart * (ROTATION_COMPLETE_RATIO / DEFAULT_FINAL_START);

    let renderedProgress = 0;
    let animationFrame = 0;
    let destroyed = false;

    /*
      固定そのものは CSS の position: sticky にネイティブで任せる。
      JS は
        - 進行度(progress)の計算
        - 円の拡大・回転（transform: scale / rotate のみ）
        - 固定解除の直前に「奥へ倒れ込む」演出（rotateX / opacityのみ。
          filterは毎フレームの再描画コストが高いため使わない）
      だけを担当する。

      sticky が祖先のoverflow設定などで無効化されている場合の検知は、
      「初期化時とリサイズ時にだけ」行う（毎フレーム判定すると、
      それ自体が余計なレイアウト読み取りとなりカクつきの原因になるため、
      常時監視はしない）。
    */

    let stickyHeight = 0;
    let paddingBottom = 0;
    let maxDiameter = 0;
    let ringMaxDiameter = 0;

    function measureLayout() {
      stickyHeight = sticky.offsetHeight;
      paddingBottom = parseFloat(getComputedStyle(section).paddingBottom) || 0;

      maxDiameter = getMaximumDiameter();
      ringMaxDiameter = maxDiameter / 0.635;

      mediaCircle.style.width = maxDiameter + "px";
      mediaCircle.style.height = maxDiameter + "px";
      textRing.style.width = ringMaxDiameter + "px";
      textRing.style.height = ringMaxDiameter + "px";
    }
    measureLayout();

    let fallbackActive = false;
    // 「stickyが固定されているべきタイミング」に実際に到達するまでは
    // 判定を確定できないため、ページ読み込み直後の1回だけでは検査できない。
    // スクロールが進んで判定可能な状態になった瞬間に一度だけ確定判定し、
    // 判定が済んだらそれ以降は検査自体を行わない（毎フレームのコスト増を避ける）。
    let stickyCheckDone = false;

    function activateFallback() {
      if (fallbackActive) return;
      fallbackActive = true;

      sticky.style.position = "absolute";
      sticky.style.top = "0";

      console.warn(
        "[join-us] position: sticky が祖先要素の overflow 設定により無効化されているため、" +
          "JS による代替の固定処理に切り替えました。可能であれば祖先の overflow 設定をご確認ください。"
      );
    }

    function checkStickyOnce(sectionRect) {
      if (stickyCheckDone || fallbackActive) return;

      // stickyが画面上端に固定されているべき区間（セクション上端は画面より上、
      // かつセクション下端はまだstickyの高さより十分下）に入るまでは判定できない。
      const shouldBePinned =
        sectionRect.top < -10 &&
        sectionRect.bottom - stickyHeight - paddingBottom > 10;

      if (!shouldBePinned) return;

      // 判定可能な状態になったので、ここで一度だけ確定判定する。
      stickyCheckDone = true;

      const stickyRect = sticky.getBoundingClientRect();
      if (Math.abs(stickyRect.top) > 10) {
        activateFallback();
      }
    }

    function computeFallbackOffset(rect) {
      const naturalTop = rect.top;
      const pinTop = 0;
      const containerBottomLimit = rect.bottom - paddingBottom - stickyHeight;

      let effectiveTop;
      if (naturalTop > pinTop) {
        effectiveTop = naturalTop;
      } else if (containerBottomLimit > pinTop) {
        effectiveTop = pinTop;
      } else {
        effectiveTop = containerBottomLimit;
      }

      return effectiveTop - naturalTop;
    }

    function render(progress, fallbackOffsetPx) {
      const normalized = clamp(progress, 0, 1);

      const scale = clamp(normalized / mediaFullAt, 0, 1);
      const mediaDiameter = maxDiameter * scale;

      const rotation =
        (Math.min(normalized, rotationCompleteAt) / rotationCompleteAt) * 360;

      anchor.style.opacity = String(
        clamp((ringMaxDiameter * scale - 40) / 40, 0, 1)
      );

      textRing.style.transform =
        "translate(-50%, -50%) scale(" + scale + ") rotate(" + rotation + "deg)";

      mediaCircle.style.transform =
        "translate(-50%, -50%) scale(" + scale + ")";

      const clipValue = "circle(" + mediaDiameter / 2 + "px at 50% 50%)";
      background.style.clipPath = clipValue;
      background.style.webkitClipPath = clipValue;

      const isFinal = normalized >= finalStart;
      section.classList.toggle("is-final", isFinal);
      finalContent.setAttribute("aria-hidden", isFinal ? "false" : "true");

      // 固定解除の直前だけ、rotateXとopacityでスッと奥に沈むような演出をつける。
      // transform と opacity はどちらもコンポジタ層だけで処理されるため、
      // filter(brightness)のような毎フレームの再描画コストが発生しない。
      const tiltProgress = clamp(
        (progress - TILT_START_RATIO) / (1 - TILT_START_RATIO),
        0,
        1
      );

      const tiltDeg = tiltProgress * TILT_MAX_DEG;
      const tiltOpacity = lerp(1, TILT_MIN_OPACITY, tiltProgress);

      const fallbackTranslate = fallbackOffsetPx
        ? "translateY(" + fallbackOffsetPx.toFixed(2) + "px) "
        : "";

      sticky.style.transform =
        fallbackTranslate +
        "perspective(1400px) rotateX(-" + tiltDeg.toFixed(2) + "deg)";
      sticky.style.opacity = String(tiltOpacity);
    }

    function getRawProgress(rect, viewportHeight) {
      return (
        (PROGRESS_START_RATIO * viewportHeight - rect.top) / viewportHeight
      );
    }

    function step() {
      // scroll位置と描画内容を常に1:1で同期させる。
      // 以前はここでlerpによる補間（追従の遅延）を行っていたが、
      // 高速スクロール時に「実際のスクロール位置」と「描画済みの見た目」が
      // ズレてしまい、遅れている間は最終コンテンツが表示されず黒く見え、
      // 追いついた瞬間に急なジャンプ（カクつき）として見える、という
      // 一つの原因から生じる二つの症状の元になっていた。
      // position: stickyによる固定自体はすでにブラウザが滑らかに行っているため、
      // JS側でさらに補間をかける必要はなく、むしろズレの原因にしかならない。
      animationFrame = 0;

      const rect = section.getBoundingClientRect();
      const viewportHeight = getViewportHeight();

      checkStickyOnce(rect);

      const fallbackOffsetPx = fallbackActive
        ? computeFallbackOffset(rect)
        : 0;

      renderedProgress = clamp(getRawProgress(rect, viewportHeight), 0, PROGRESS_MAX);
      render(renderedProgress, fallbackOffsetPx);
    }

    function requestUpdate() {
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(step);
    }

    function onResize() {
      measureLayout();
      checkStickyOnce(section.getBoundingClientRect());
      requestUpdate();
    }

    function destroy() {
      destroyed = true;

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", onResize);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onResize);
      }

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      sticky.style.transform = "";
      sticky.style.opacity = "";

      if (fallbackActive) {
        sticky.style.position = "";
        sticky.style.top = "";
      }

      delete section.dataset.joinUsInitialized;
      delete section._joinUsDestroy;
    }

    (function primeInitialState() {
      const rect = section.getBoundingClientRect();
      const viewportHeight = getViewportHeight();

      checkStickyOnce(rect);

      const fallbackOffsetPx = fallbackActive ? computeFallbackOffset(rect) : 0;

      renderedProgress = clamp(getRawProgress(rect, viewportHeight), 0, PROGRESS_MAX);
      render(renderedProgress, fallbackOffsetPx);
    })();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize, { passive: true });
    }

    section._joinUsDestroy = destroy;
  }

  function initializeAll(root) {
    const target = root || document;

    if (target.matches && target.matches(ROOT_SELECTOR)) {
      initializeJoinUs(target);
    }

    target.querySelectorAll(ROOT_SELECTOR).forEach(initializeJoinUs);
  }

  function start() {
    initializeAll(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initializeAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    const section = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector(ROOT_SELECTOR);

    if (section && typeof section._joinUsDestroy === "function") {
      section._joinUsDestroy();
    }
  });
})();
